/**
 * polaroidExport — renders a Memory Snap to a 1080×1350 PNG client-side.
 *
 * Per Craig's spec: "Do NOT screenshot the live wall. Generate exports
 * separately... render engine rebuilds wall composition cleanly". For
 * the individual Polaroid this means we draw to <canvas> from the raw
 * primitives (photo URL + message + pen colour + seed) rather than
 * html2canvas-ing the DOM. Result is print-safe, deterministic,
 * cross-browser, and zero dependency.
 *
 * The text variance algorithm mirrors HandwrittenText.js exactly so the
 * download looks visually identical to the on-screen polaroid.
 */

const W = 1080;
const H = 1350;
const PAD = 36;
const PHOTO_H_RATIO = 0.74;

// Mulberry32 — must match HandwrittenText's RNG byte-for-byte so a
// given seed produces the same imperfections in DOM and canvas.
const seeded = (seed) => {
  let s = seed | 0;
  return () => {
    s = (s + 0x6D2B79F5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};
const range = (rng, mn, mx) => mn + rng() * (mx - mn);

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});

/**
 * exportPolaroidPng({ photoUrl, message, penColor, seed }) → Blob
 */
export const exportPolaroidPng = async ({ photoUrl, message, penColor, seed = 1 }) => {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Paper background
  ctx.fillStyle = '#FCFCF7';
  ctx.fillRect(0, 0, W, H);
  // Faint paper texture — three subtle radials.
  for (const [cx, cy, r, alpha] of [[W*0.22, H*0.18, W*0.5, 0.04], [W*0.78, H*0.82, W*0.55, 0.035]]) {
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    grad.addColorStop(0, `rgba(212,175,94,${alpha})`);
    grad.addColorStop(1, 'rgba(212,175,94,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);
  }

  // Photo region
  const photoH = (H - PAD * 2) * PHOTO_H_RATIO;
  const photoW = W - PAD * 2;
  ctx.fillStyle = '#000';
  ctx.fillRect(PAD, PAD, photoW, photoH);
  try {
    const img = await loadImage(photoUrl);
    // cover-fit
    const iar = img.width / img.height;
    const par = photoW / photoH;
    let dw = photoW, dh = photoH, dx = PAD, dy = PAD, sx = 0, sy = 0, sw = img.width, sh = img.height;
    if (iar > par) {
      sw = img.height * par;
      sx = (img.width - sw) / 2;
    } else {
      sh = img.width / par;
      sy = (img.height - sh) / 2;
    }
    ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
  } catch {
    // Photo load failed — leave the black backdrop, render anyway.
  }

  // Subtle photo vignette so the download matches the on-screen look.
  const vg = ctx.createRadialGradient(W/2, PAD + photoH/2, photoH*0.2, W/2, PAD + photoH/2, photoH*0.7);
  vg.addColorStop(0, 'rgba(0,0,0,0)');
  vg.addColorStop(1, 'rgba(0,0,0,0.25)');
  ctx.fillStyle = vg;
  ctx.fillRect(PAD, PAD, photoW, photoH);

  // ── Handwritten message ───────────────────────────────────────────
  const fontSize = Math.round(W * 0.072);
  ctx.fillStyle = penColor;
  ctx.font = `500 ${fontSize}px Caveat, "Patrick Hand", cursive`;
  ctx.textBaseline = 'top';

  // Wrap text into lines that fit within the message width.
  const msgX = PAD + W * 0.024;
  const msgY = PAD + photoH + Math.round(W * 0.036);
  const maxWidth = photoW - W * 0.048;
  const words = String(message || '').split(/\s+/);
  const lines = [];
  let cur = '';
  for (const w of words) {
    const trial = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(trial).width > maxWidth && cur) {
      lines.push(cur);
      cur = w;
    } else {
      cur = trial;
    }
  }
  if (cur) lines.push(cur);

  // Render each line with per-char variance using a seeded RNG.
  const rng = seeded(seed || 1);
  const lineHeight = fontSize * 1.18;
  lines.forEach((ln, li) => {
    const lineRotation = range(rng, -0.8, 0.8) * Math.PI / 180;
    const lineYDrift = range(rng, -0.5, 0.5);
    ctx.save();
    ctx.translate(msgX, msgY + li * lineHeight + lineYDrift);
    ctx.rotate(lineRotation);
    let xCursor = 0;
    for (const ch of ln) {
      const r = range(rng, -1.2, 1.2) * Math.PI / 180;
      const dy = range(rng, -1, 2);
      const s = range(rng, 0.98, 1.03);
      const ls = range(rng, -0.4, 0.4);
      const opacity = 0.93 + (Math.abs(r) / (Math.PI/150)) * 0.005;
      ctx.save();
      ctx.translate(xCursor, dy);
      ctx.rotate(r);
      ctx.scale(s, s);
      ctx.globalAlpha = Math.min(1, opacity);
      ctx.fillText(ch === ' ' ? '\u00A0' : ch, 0, 0);
      ctx.restore();
      const adv = ctx.measureText(ch === ' ' ? ' ' : ch).width * s;
      xCursor += adv + ls + 0.5;
    }
    ctx.restore();
  });

  // EventDrop subtle watermark — bottom-right, very faint. Per product
  // direction the caption itself is guest-authored, so the visual
  // attribution stays quiet — readable up close, invisible at a glance.
  ctx.fillStyle = 'rgba(120,110,90,0.32)';
  ctx.font = '500 16px Inter, system-ui, sans-serif';
  ctx.textAlign = 'right';
  ctx.fillText('eventdrop.co', W - PAD - 6, H - PAD - 6);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/png', 0.95);
  });
};

// ── filename helpers ───────────────────────────────────────────────
const slugify = (s) => String(s || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/(^-|-$)/g, '')
  .slice(0, 40) || 'event';

/**
 * buildSnapFilename — `memory-snap-{event-slug}-{YYYY-MM-DD}.png` so the
 * saved file makes immediate sense in the guest's camera roll years
 * later (matches the long-tail emotional value of the product).
 */
export const buildSnapFilename = (event, snap) => {
  const eventPart = slugify(event?.event_name || event?.slug);
  const ts = snap?.created_at ? new Date(snap.created_at) : new Date();
  const isoDate = isNaN(ts.getTime()) ? new Date().toISOString().slice(0, 10) : ts.toISOString().slice(0, 10);
  return `memory-snap-${eventPart}-${isoDate}.png`;
};

// iOS Safari blocks programmatic anchor-click downloads of blob: URLs —
// it just navigates the current tab to the blob and strips the
// download attribute. Detect once and route through a same-tab open
// so the system share sheet appears instead (user can then "Save Image").
const isIosSafari = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIos = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && 'ontouchend' in document);
  const isSafari = /^((?!chrome|crios|fxios|edgios).)*safari/i.test(ua);
  return isIos && isSafari;
};

/**
 * downloadPolaroid — convenience wrapper that triggers a save dialog.
 *
 * Returns `{ ok, blob, filename }` so the caller can decide whether to
 * surface a "Saved to your photos 💛" toast vs. an error state.
 *
 * On iOS Safari we open the blob in a new tab (rather than relying on
 * the broken `download` attribute) so the user gets the native share
 * sheet and can save to Photos. Everywhere else uses the standard
 * anchor-click pattern.
 */
export const downloadPolaroid = async (snap, filenameOrOpts) => {
  // Back-compat: callers used to pass a plain filename string.
  const opts = typeof filenameOrOpts === 'string'
    ? { filename: filenameOrOpts }
    : (filenameOrOpts || {});
  const event = opts.event || null;
  const filename = opts.filename || buildSnapFilename(event, snap);

  try {
    const blob = await exportPolaroidPng({
      photoUrl: snap.photo_url,
      message: snap.message,
      penColor: snap.pen_hex || snap.pen_color,
      seed: snap.style_seed || 1,
    });
    if (!blob) return { ok: false };

    const url = URL.createObjectURL(blob);
    if (isIosSafari()) {
      // iOS: open the blob — user long-presses → Save to Photos.
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } else {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    }
    return { ok: true, blob, filename };
  } catch (err) {
    console.error('[polaroidExport] download failed', err);
    return { ok: false, error: err };
  }
};

/**
 * polaroidToFile — render the snap to a Blob → File so it can be
 * attached to `navigator.share({ files })`. Returns null if the export
 * fails so the caller can fall back to a URL/caption-only share.
 */
export const polaroidToFile = async (snap, event) => {
  try {
    const blob = await exportPolaroidPng({
      photoUrl: snap.photo_url,
      message: snap.message,
      penColor: snap.pen_hex || snap.pen_color,
      seed: snap.style_seed || 1,
    });
    if (!blob) return null;
    return new File([blob], buildSnapFilename(event, snap), { type: 'image/png' });
  } catch {
    return null;
  }
};
